// Shared Hinglish voice input helper — Web Speech API (Chrome/Edge/Safari)
// Usage: <VoiceInputHelper onDictate={(text) => { ...parse... }} lang="hi-IN" />
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

export default function VoiceInputHelper({ onDictate, lang = 'hi-IN', label = 'Voice' }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recRef = useRef(null);
  const finalRef = useRef('');
  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input not supported (Chrome/Edge/Safari)'); return; }
    const rec = new SR();
    rec.lang = lang; rec.interimResults = true; rec.continuous = false;
    finalRef.current = '';
    rec.onstart = () => { setListening(true); setTranscript(''); };
    rec.onresult = (e) => {
      // Rebuild finals + interim from scratch each event — avoids duplication when
      // Chrome re-fires the same result index or finalises an interim segment.
      let final = ''; let interim = '';
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + ' '; else interim += t;
      }
      finalRef.current = final.trim();
      setTranscript((finalRef.current + ' ' + interim).replace(/\s+/g, ' ').trim());
    };
    rec.onerror = () => { setListening(false); toast.error('🎤 Voice error, retry'); };
    rec.onend = () => setListening(false);
    recRef.current = rec; rec.start();
  };
  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } };
  const commit = () => {
    if (!transcript) { toast.error('Nothing spoken yet'); return; }
    onDictate?.(transcript);
    setTranscript('');
  };
  return (
    <div className="flex items-center gap-1" data-testid="voice-helper">
      {!listening && !transcript && (
        <button onClick={start} data-testid="voice-start" type="button" className="text-[10px] px-2 py-1 rounded bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-black">🎤 {label}</button>
      )}
      {listening && (
        <button onClick={stop} data-testid="voice-stop" type="button" className="text-[10px] px-2 py-1 rounded bg-red-500 text-white font-black animate-pulse">⏹ Stop</button>
      )}
      {transcript && !listening && (
        <>
          <div className="text-[10px] px-2 py-1 rounded bg-slate-800 border border-slate-700 max-w-[180px] truncate">{`"${transcript}"`}</div>
          <button onClick={commit} data-testid="voice-commit" type="button" className="text-[10px] px-2 py-1 rounded bg-emerald-500 text-black font-black">✓ Use</button>
          <button onClick={() => setTranscript('')} type="button" className="text-[10px] px-1.5 py-1 rounded bg-slate-700 text-slate-200">✕</button>
        </>
      )}
    </div>
  );
}

// Shared parser: extracts numeric fields from Hinglish/Hindi/English text
// keywords: { key: ['bp', 'blood pressure', 'ब्लड प्रेशर'] } → returns { key: value }
export function parseNumericFields(text, keywords) {
  const t = (text || '').toLowerCase();
  const out = {};
  for (const [key, aliases] of Object.entries(keywords)) {
    for (const a of aliases) {
      const re = new RegExp(`${a}\\s*(?:is|se|hai|=)?\\s*([\\d.]+(?:\\s*(?:by|slash|\\/|बटा|पर)\\s*[\\d.]+)?)`, 'i');
      const m = t.match(re);
      if (m) { out[key] = m[1].trim(); break; }
    }
  }
  return out;
}
